/**
 * TreeService — the client's in-RAM catalog tree, keyed by node id (spec §3.5).
 *
 * Replaces the deleted name-/level-based `ModelFinderCascade` + `/io`
 * `ProgressiveDataLoader`. The catalog is a content-addressed HTTP resource
 * (CDN + browser-cached); this service is the RAM-side index of whatever
 * subtrees have been fetched, plus the single-id derivations the controllers
 * consume:
 *   - `ensureChildren(parentId, depth)` — fetch + merge a subtree (deduped by
 *     `loaded`), adopt the fresh hash after a 302, surface `levelLabels`;
 *   - `pathTo(id)` — the cached spine root→id (single-id path reconstruction);
 *   - `childrenOf(parentId)` / `node(id)` / `labels()`.
 *
 * Catalog data is RAM-only — never written to `localStorage` (ADR-0004). The
 * version hash is read from the page attribute and refreshed from each
 * response's `finalUrl`; the client never constructs it.
 *
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.5
 */

import { callEndpoint, mfSubtree, versionHashFromUrl } from './endpoint';
import { compareNodes } from '../domain/node-sort';
import type { Level } from '../domain/levels';
import { ROOT_ID, type NodeId, type SubtreeHash } from '../domain/brand';
import type { LevelLabelOverride, Node } from './wire';

// ROOT_ID now lives in domain/brand.ts (pre-branded NodeId); re-exported here for
// back-compat with existing `import { ROOT_ID } from './tree-service'` callers.
export { ROOT_ID } from '../domain/brand';

export class TreeService {
  private readonly nodes = new Map<NodeId, Node>();
  /** parentId (null=root) → max child-depth already fetched (the dedup key). */
  private readonly loaded = new Map<NodeId | null, number>();
  /** Latest per-level title overrides (last-write-wins, merged over /levels). */
  private levelLabels: LevelLabelOverride = {};
  /**
   * The column plan adopted per `requestedId` (D6). A plan REPLACES (it is the
   * full visible list for that id), never merges like `levelLabels`. The
   * controller reads the plan for the current deepest `selectedId`, so the
   * restore fan-out's concurrent shallower-parent responses cannot win.
   */
  private readonly columnPlans = new Map<NodeId, Level[]>();

  /** @param subtreeHash seeded from `data-mf-subtree-hash` (spec §3.8). */
  constructor(private subtreeHash: SubtreeHash) {}

  /** The version hash currently in use (refreshes after a 302). */
  get hash(): SubtreeHash {
    return this.subtreeHash;
  }

  /**
   * Fetch + merge a subtree (deduped by `loaded`), adopt a fresh hash after a
   * 302, and surface any `levelLabels`. Returns the children of `parentId`.
   */
  async ensureChildren(parentId: NodeId | null, depth = 1): Promise<Node[]> {
    if ((this.loaded.get(parentId) ?? 0) < depth) {
      const res = await callEndpoint(mfSubtree, {
        hash: this.subtreeHash,
        id: parentId ?? ROOT_ID,
        depth,
      });
      // Adopt the fresh hash the redirect resolved to (stale → 302 → current).
      this.subtreeHash = versionHashFromUrl(res.finalUrl) ?? this.subtreeHash;
      for (const n of res.ancestors) this.nodes.set(n.id, n); // spine for path reconstruction
      for (const n of Object.values(res.nodes)) this.nodes.set(n.id, n);
      if (res.levelLabels) this.levelLabels = { ...this.levelLabels, ...res.levelLabels };
      // Adopt the column plan keyed by the response's requestedId (D6): a plan
      // REPLACES the prior plan for that id (it is the full visible list).
      this.columnPlans.set(res.requestedId, res.columns);
      this.loaded.set(parentId, depth);
    }
    return this.childrenOf(parentId);
  }

  /** From a single deepest id: the cached path root→selected (spine + self). */
  pathTo(id: NodeId): Node[] {
    const path: Node[] = [];
    let cur = this.nodes.get(id);
    while (cur) {
      path.unshift(cur);
      cur = cur.parentId === null ? undefined : this.nodes.get(cur.parentId);
    }
    return path;
  }

  /**
   * The cached children of `parentId` (null = the top-level / column 1),
   * naturally sorted by display name (`compareNodes`). The wire keys `nodes` by
   * id, so without this the view would render in numeric-id order; sorting here
   * — the single derivation point used by both finders — fixes it once. A copy
   * is sorted; the backing map order is left untouched.
   */
  childrenOf(parentId: NodeId | null): Node[] {
    return [...this.nodes.values()].filter((n) => n.parentId === parentId).sort(compareNodes);
  }

  /** A cached node by id, or `undefined`. */
  node(id: NodeId): Node | undefined {
    return this.nodes.get(id);
  }

  /** The latest merged per-level title overrides. */
  labels(): LevelLabelOverride {
    return this.levelLabels;
  }

  /**
   * The column plan to render for `forId`. Prefers the plan adopted from the
   * response whose `requestedId === forId`; when none was fetched (a LIVE click
   * on a model leaf never fetches its own subtree — AC-S10), falls back **up the
   * cached spine** to the deepest ANCESTOR with an adopted plan. Returns `[]`
   * ONLY for a genuinely unknown id (empty spine).
   *
   * This guarantees a known path never blanks the columns (Bug #2, D25): the
   * parent's adopted plan is already the spine-inclusive full list for that
   * branch (the leaf's column was populated from the parent's response), so it
   * is exactly the leaf's plan. The controller stays dumb — it always reads
   * `columnPlan(selectedId)` (D6) and never owns the fallback.
   *
   * GOTCHA — one KNOWN node can still yield `[]`: a **top-level childless** node
   * (a manufacturer whose sub-categories are not yet synced from the ERP). Its
   * spine is only itself, and the root plan is keyed under `ROOT_ID`, which is
   * never a spine member — so the walk above finds nothing.
   * Do NOT "fix" this by falling back to the ROOT plan: the root plan is NOT that
   * node's plan (live: root `[1,6]` vs the node's own `[1]`), so that would render
   * a phantom, permanently empty trailing column. D25's "parent plan ≡ child plan"
   * argument breaks at the root boundary. `ModelFinderCore.onSelect` compensates
   * by fetching the node's own subtree exactly when this returns `[]` — that
   * caller DEPENDS on the empty return, so changing it here silently disables the
   * fix (blank finder returns). @see ADR-0009 Decision History 2026-09-25.
   *
   * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/spec.md §3.6
   */
  columnPlan(forId: NodeId): Level[] {
    const direct = this.columnPlans.get(forId);
    if (direct) return direct;
    // Fall back UP the cached spine to the nearest ancestor with an adopted plan.
    const spine = this.pathTo(forId); // root→forId inclusive (or [] if unknown)
    for (let i = spine.length - 1; i >= 0; i--) {
      const plan = this.columnPlans.get(spine[i].id);
      if (plan) return plan;
    }
    return []; // unknown id only
  }

  /**
   * Drop per-level title overrides DEEPER than `level`. Called when a parent
   * selection changes: the deeper columns are about to be re-populated, so a
   * stale override (e.g. a level-5 "Serie 1" from a previously selected branch)
   * must not linger over the new column until its own series_1 is selected. The
   * selection-based level-5 override is re-emitted by the newly selected
   * series_1's subtree response (spec §3.5).
   */
  resetLabelsAfter(level: number): void {
    for (const key of Object.keys(this.levelLabels)) {
      if (Number(key) > level) delete this.levelLabels[key];
    }
  }
}
